import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import type { StepSectionStatus } from "../../domain/getReadyTasks/journeyProgressAdapter";
import styles from "./GetReadyStepSection.module.css";
import { JourneyProgressCircle } from "./JourneyProgressCircle";

export interface GetReadyStepSectionProps {
	stepName: string;
	stepStatus: StepSectionStatus;
	stepOrder: number;
	totalSteps: number;
	defaultOpen?: boolean;
	/**
	 * When set to true, forces the section open (e.g. deep-link focus).
	 * Ignored on subsequent renders once `false` again — intentionally
	 * edge-triggered so the user can still manually collapse it.
	 */
	forceOpen?: boolean;
	expandLabel: string;
	collapseLabel: string;
	/** Localized aria-label for the progress circle (e.g. "Step 2 of 7"). */
	progressLabel: string;
	taskCount: number;
	/** D2C has no progress UI — hide the step progress circle when true. */
	hideProgress?: boolean;
	children: ReactNode;
}

export function GetReadyStepSection({
	stepName,
	stepStatus,
	stepOrder,
	totalSteps,
	defaultOpen = false,
	forceOpen = false,
	expandLabel,
	collapseLabel,
	progressLabel,
	taskCount,
	hideProgress = false,
	children,
}: GetReadyStepSectionProps) {
	const [open, setOpen] = useState(defaultOpen);

	// Edge-triggered: whenever forceOpen flips to true, expand the section.
	// useEffect runs after paint so the children are already in the DOM when
	// the caller's scroll effect fires in the same flush.
	useEffect(() => {
		if (forceOpen) setOpen(true);
	}, [forceOpen]);
	const bodyId = useId();

	const isEmpty = taskCount === 0;
	const effectiveOpen = isEmpty ? false : open;

	return (
		<section
			className={styles.section}
			data-step-status={stepStatus}
			data-open={effectiveOpen ? "true" : "false"}
		>
			<button
				type="button"
				className={styles.header}
				aria-expanded={effectiveOpen}
				aria-controls={bodyId}
				aria-label={effectiveOpen ? collapseLabel : expandLabel}
				onClick={() => {
					if (!isEmpty) setOpen((prev) => !prev);
				}}
				disabled={isEmpty}
			>
				{!hideProgress && (
					<JourneyProgressCircle
						current={stepOrder}
						total={totalSteps}
						ariaLabel={progressLabel}
					/>
				)}
				<span className={styles.headerText}>
					<span className={styles.stepName}>{stepName}</span>
				</span>
				<span
					className={`${styles.chevron} ${effectiveOpen ? styles.chevronOpen : ""}`}
					aria-hidden="true"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 16 16"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
						focusable="false"
					>
						<title>{effectiveOpen ? collapseLabel : expandLabel}</title>
						<path
							d="M4 6L8 10L12 6"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			</button>
			{effectiveOpen ? (
				<div id={bodyId} className={styles.body}>
					{children}
				</div>
			) : null}
		</section>
	);
}
